import './ContextMenu.scss';
import { Component } from '@components/Component/Component.js';
import DIContainer, { getInstance } from '@components/DependencyInjectionContainer.js';
import { createElement, getID, isMobile } from '@components/Utility/index.js';
/**
 * Class `ContextMenu` provides a generic context menu with the ability to have sub-menus.
 */
export class ContextMenu extends Component {
    get IconManager() {
        if (!this.dependencies.IconManager) {
            // we didnt receive a IconManager dependency injection, so just use the global singleton
            this.dependencies.IconManager = getInstance('IconManager');
        }
        return this.dependencies.IconManager;
    }
    constructor(id, rootElement, dependencies = {}) {
        super(id, rootElement, dependencies);
        this.componentName = 'ContextMenu';
        this.rootElementType = 'div';
        this.registeredComponents = new Set();
        this.registeredMenuItemEventIds = [];
        this.isSheetMode = false;
        this.menuStack = [];
        this.hotkeys = {
            'arrowup': () => {
                this.navigateMenu('up');
                return true;
            },
            'arrowdown': () => {
                this.navigateMenu('down');
                return true;
            },
            'arrowright': () => {
                this.navigateMenu('right');
                return true;
            },
            'arrowleft': () => {
                this.navigateMenu('left');
                // this.navigateOutOfSubMenu();
                return true;
            },
            'enter': () => {
                this.selectActiveItem();
                return true;
            },
            'escape': () => {
                this.clickAway();
                return true;
            }
        };
        this.lastMenuId = null;
        this.lastClosedTime = 0;
        this.menuElement = createElement('div', ['context-menu', 'top-menu']);
        this.subMenuContainer = createElement('div', ['submenu-container']);
        this.menuElement.tabIndex = 0; // make it focusable
        this.hiddenProperty = 'visibility';
    }
    /**
     * `Component`s that support a context menu will register themselves so that click events can be delegated accordingly
     * @param component
     */
    register(component) {
        this.registeredComponents.add(component);
    }
    /**
     * `Component`s that support a context menu will unregister themselves when they are destroyed
     * @param component
     */
    unregister(component) {
        this.registeredComponents.delete(component);
    }
    handleFullscreenSwitch(fullscreenElement) {
        super.handleFullscreenSwitch(fullscreenElement);
        if (fullscreenElement) {
            fullscreenElement.appendChild(this.rootElement);
        }
        else {
            document.body.appendChild(this.rootElement);
        }
    }
    draw() {
        super.draw();
        this.hide();
        document.body.appendChild(this.rootElement);
        // event listeners to initiate showing the context menu 
        this.registerEvent('contextmenu', document, this.delegateEvent('handleRightClick'));
        this.registerEvent('touchstart', document, this.delegateEvent('handleLongPress'));
        this.registerEvent('touchend', document, this.delegateEvent('handleTouchEnd'));
        // Global listener to close the context menu when clicking outside
        // this.clickAwayId = this.registerEvent('click', document, (event) => {
        // 	if (!this.rootElement.contains(event.target as HTMLElement)) {
        // 		this.clickAway();
        // 	}
        // }, {capture: true});
    }
    /**
     * A single instance of `ContextMenu` can serve many components by delegating events when it detects right clicks etc.
     * This allows the component to provide its own implementation of criteria to display the context menu.
     * This is mostly done in `ContextMenuMixin`
     * @param eventName
     * @private
     */
    delegateEvent(eventName) {
        return (event) => {
            if (event instanceof MouseEvent && event.shiftKey) {
                return event;
            }
            //  We should traverse up the DOM from our event target and find containers that have data-global-id, and then see if that component exists in our registered components.
            // this only occurs during right click events so its not a concern from a performance perspective. 
            let target = event.target;
            while (target /* && target !== this.rootElement*/) {
                if (target.dataset && target.dataset.globalId) {
                    const component = DIContainer.getInstance('ComponentRegistry').getComponent(target.dataset.globalId);
                    if (component && this.registeredComponents.has(component)) {
                        if (component && typeof component[eventName] === 'function') {
                            component[eventName](event);
                            break;
                        }
                    }
                }
                target = target.parentNode;
            }
        };
    }
    /**
     * hide the menu when clicking away from it
     * @protected
     */
    clickAway() {
        this.hide();
        this.resetMenu();
        this.menuStack = [];
        this.isSheetMode = false;
        this.lastClosedTime = performance.now();
        if (this.clickAwayId) {
            this.unRegisterEvent(this.clickAwayId);
        }
    }
    resetMenu() {
        this.menuElement.innerHTML = '';
        this.subMenuContainer.innerHTML = '';
        this.clearRegisteredEvents();
    }
    /**
     * Display the context menu at a given position, with a given array of `ContextMenuItem`s
     * @param items - items to display which may also contain subitems
     * @param contextInfo - additional parameters that provide context for the click handlers to decide what to do
     */
    showMenu(items, contextInfo, menuId) {
        const now = performance.now();
        // If we're trying to reopen the same menu within 75ms → ignore
        if (menuId && this.lastMenuId === menuId && (now - this.lastClosedTime < 75)) {
            this.lastMenuId = null;
            return;
        }
        this.lastMenuId = menuId || null;
        const sheetDefault = isMobile() && !contextInfo.autocomplete;
        this.isSheetMode = (contextInfo.mobileSheet ?? sheetDefault) && isMobile();
        this.currentContextInfo = contextInfo;
        this.clickAwayId = this.registerEvent('click', document, (event) => {
            if (!this.rootElement.contains(event.target)) {
                this.clickAway();
            }
        }, { capture: true });
        if (contextInfo.initiator && !contextInfo.form) {
            contextInfo.form = DIContainer.getInstance('ComponentRegistry').getFormComponentByElement(contextInfo.initiator.rootElement);
        }
        // this.resetMenu();
        this.menuStack = [];
        if (this.isSheetMode) {
            // Root level lives at index 0 while menu is open
            this.menuStack.push({ items, title: contextInfo.sheetTitle ?? '' });
            this.renderMenu(items, contextInfo, this.menuStack[0].title);
        }
        else {
            this.renderMenu(items, contextInfo);
        }
        this.renderMenu(items, contextInfo);
        // items.forEach(item => this.menuElement.appendChild(this.createMenuItem(item, contextInfo)));
        contextInfo.event.stopImmediatePropagation();
        if (this.isSheetMode) {
            this.applySheetMode(contextInfo);
        }
        else {
            this.applyFloatingMode(contextInfo);
        }
        // this.setMenuPosition(contextInfo);
        this.show();
        if (!contextInfo.autocomplete) {
            this.menuElement.focus();
        }
    }
    renderMenu(items, contextInfo, title) {
        this.resetMenu();
        // this.menuElement.innerHTML = '';
        // this.subMenuContainer.innerHTML = '';
        let itemsContainer = this.menuElement;
        if (this.isSheetMode) {
            const header = createElement('div', ['contextmenu-sheet-header']);
            if (this.menuStack.length > 1) {
                const back = createElement('button', ['contextmenu-sheet-back']);
                back.appendChild(this.IconManager.getIconElement('chevron_left'));
                this.registeredMenuItemEventIds.push(this.registerEvent('click', back, (e) => {
                    e.stopPropagation();
                    this.popSheetMenu();
                }));
                header.appendChild(back);
            }
            const headerTitle = createElement('div', ['contextmenu-sheet-title']);
            headerTitle.innerText = title || '';
            header.appendChild(headerTitle);
            const close = createElement('button', ['contextmenu-sheet-close']);
            close.appendChild(this.IconManager.getIconElement('close'));
            this.registeredMenuItemEventIds.push(this.registerEvent('click', close, (e) => {
                e.stopPropagation();
                this.clickAway();
            }));
            header.appendChild(close);
            this.menuElement.appendChild(header);
            itemsContainer = createElement('div', ['contextmenu-sheet-items']);
            this.menuElement.appendChild(itemsContainer);
        }
        items.forEach(item => itemsContainer.appendChild(this.createMenuItem(item, contextInfo)));
    }
    pushSheetMenu(items, title) {
        this.menuStack.push({ items, title });
        this.renderMenu(items, this.currentContextInfo, title);
    }
    popSheetMenu() {
        // If we're already at root, do nothing
        if (this.menuStack.length <= 1) {
            const root = this.menuStack[0];
            this.renderMenu(root.items, this.currentContextInfo, root.title);
            return;
        }
        // Pop current submenu
        this.menuStack.pop();
        // Render previous level
        const prev = this.menuStack[this.menuStack.length - 1];
        this.renderMenu(prev.items, this.currentContextInfo, prev.title);
    }
    applySheetMode(contextInfo) {
        this.rootElement.classList.add('mobile-sheet');
        this.rootElement.classList.remove('v-hidden');
        const h = contextInfo.mobileSheetHeight ?? 0.55;
        this.rootElement.style.setProperty('--sheet-max-height', `${Math.round(h * 100)}vh`);
        // no x/y positioning in sheet mode
        this.rootElement.style.left = '';
        this.rootElement.style.right = '';
        this.rootElement.style.top = '';
    }
    applyFloatingMode(contextInfo) {
        this.rootElement.classList.remove('mobile-sheet');
        this.setMenuPosition(contextInfo);
    }
    createMenuItem(item, contextInfo, parentIdChain = '') {
        const menuItem = createElement('div', ['menu-item']);
        if (item.color) {
            menuItem.classList.add(`color-${item.color}`);
        }
        if (item.classList) {
            menuItem.classList.add(...item.classList);
        }
        if (item.itemtype === 'SEPARATOR') {
            menuItem.classList.add('separator');
            const separator = createElement('hr');
            menuItem.appendChild(separator);
            return menuItem;
        }
        const itemText = createElement('span', ['menu-item-text']);
        itemText.innerText = item.caption || '';
        menuItem.appendChild(itemText);
        if (item.icon) {
            const icon = this.IconManager.getIconElement(item.icon);
            itemText.classList.add('has-icon');
            itemText.prepend(icon);
        }
        if (item.disabled) {
            menuItem.classList.add('disabled');
        }
        if (item.selected) {
            menuItem.classList.add('selected');
        }
        if (item.hotkey) {
            const hotkeyText = createElement('span', ['menu-item-hotkey']);
            hotkeyText.innerText = item.hotkey;
            menuItem.appendChild(hotkeyText);
        }
        if (!item.componentid) {
            item.componentid = getID((item.caption || this.id).replaceAll(' ', ''));
        }
        menuItem.setAttribute('data-componentid', item.componentid);
        menuItem.dataset.parentIdChain = parentIdChain;
        /*
        if (item.children && item.children.length) {
            this.addSubMenu(menuItem, item.children, contextInfo, parentIdChain);
        } else {
            this.registerMenuHoverEvents(menuItem);
        }*/
        if (item.children && item.children.length) {
            if (this.isSheetMode) {
                // drill-in instead of flyout
                menuItem.classList.add('flyout');
                const arrowIcon = this.IconManager.getIconElement('chevron_right');
                menuItem.appendChild(arrowIcon);
                this.registeredMenuItemEventIds.push(this.registerEvent('click', menuItem, (e) => {
                    e.stopPropagation();
                    if (item.disabled)
                        return;
                    // push children into same list
                    this.pushSheetMenu(item.children, item.caption || '');
                }, { capture: true }));
            }
            else {
                this.addSubMenu(menuItem, item.children, contextInfo, parentIdChain);
            }
        }
        else {
            this.registerMenuHoverEvents(menuItem);
        }
        if (!item.disabled && item.onClick) {
            this.registeredMenuItemEventIds.push(this.registerEvent('click', menuItem, (event) => this.handleClick(event, item.onClick, contextInfo), { capture: true }));
        } /*else if (item.children && isMobile()) {
            this.registeredMenuItemEventIds.push(
                this.registerEvent('click', menuItem, (event: MouseEvent) => {
                    event.stopPropagation();
                    menuItem.classList.toggle('active');
                })
            );
        } */
        else if (item.disabled && item.disabledMessage) {
            this.registeredMenuItemEventIds.push(this.registerEvent('click', menuItem, () => {
                DIContainer.getInstance('Application').alert({ caption: item.disabledMessage });
                this.clickAway();
            }));
        }
        return menuItem;
    }
    handleClick(event, onClick, contextInfo) {
        event.stopPropagation();
        onClick(contextInfo);
        this.clickAway();
    }
    addSubMenu(menuItem, children, contextInfo, parentIdChain) {
        const arrowIcon = this.IconManager.getIconElement('chevron_right');
        menuItem.appendChild(arrowIcon);
        menuItem.classList.add('flyout');
        const subMenu = createElement('div', ['context-menu', 'sub-menu', 'v-hidden']);
        subMenu.dataset.parentId = menuItem.dataset.componentid;
        parentIdChain += ',' + subMenu.dataset.parentId;
        subMenu.dataset.parentIdChain = parentIdChain;
        children.forEach(subItem => {
            const child = this.createMenuItem(subItem, contextInfo, parentIdChain);
            subMenu.appendChild(child);
        });
        this.subMenuContainer.appendChild(subMenu);
        this.registerMenuHoverEvents(menuItem, subMenu);
    }
    registerMenuHoverEvents(menuItem, subMenu) {
        // TODO: one mouseover event on the menu container would be good enough, similar to select.ts etc
        this.registeredMenuItemEventIds.push(this.registerEvent('mouseover', menuItem, (e) => {
            if (subMenu) {
                if (!subMenu.contains(e.target)) {
                    e.stopImmediatePropagation();
                    this.subMenuHover(subMenu, menuItem);
                    this.clearActive(menuItem);
                }
            }
            else {
                menuItem.classList.add('active');
                this.closeFlyouts(menuItem);
                this.clearActive(menuItem);
            }
        }));
        if (isMobile() && subMenu) {
            this.registeredMenuItemEventIds.push(this.registerEvent('click', menuItem, (e) => {
                e.stopPropagation();
                if (e.target === menuItem) {
                    this.subMenuHover(subMenu, menuItem);
                }
            }));
        }
    }
    clearRegisteredEvents() {
        this.registeredMenuItemEventIds.forEach(eventId => this.unRegisterEvent(eventId));
        this.registeredMenuItemEventIds.length = 0;
    }
    subMenuHover(subMenu, menuItem) {
        subMenu.classList.remove('v-hidden');
        menuItem.classList.add('active');
        subMenu.removeAttribute('style');
        const itemRect = menuItem.getBoundingClientRect();
        const subMenuRect = subMenu.getBoundingClientRect();
        const rootRect = this.rootElement.getBoundingClientRect();
        // Default positioning: to the right of the menu item
        let left = itemRect.right;
        let top = itemRect.top;
        if (left + subMenuRect.width > window.innerWidth) {
            left = itemRect.left - subMenuRect.width; // Position to the left of the parent item
        }
        let maxHeight = window.innerHeight - itemRect.top - 15;
        // Adjust position if the submenu would go off the bottom of the screen
        if (maxHeight < subMenuRect.height && itemRect.top - 15 > maxHeight) {
            maxHeight = itemRect.top - 15;
            top = 0 - Math.min(maxHeight, subMenuRect.height) + itemRect.height - (rootRect.top - itemRect.top);
        }
        else {
            top -= rootRect.top;
        }
        left -= rootRect.left;
        subMenu.style.left = `${left}px`;
        subMenu.style.top = `${top}px`;
        subMenu.style.maxHeight = `${maxHeight}px`;
        this.closeFlyouts(menuItem);
    }
    closeFlyouts(skipIfContainsElement) {
        // Iterate through the open flyouts
        const openFlyouts = this.rootElement.querySelectorAll('.flyout.active');
        openFlyouts.forEach((flyout) => {
            const submenu = this.submenuElementByParentIdGet(flyout.dataset.componentid);
            if (!this.menuContainsElement(skipIfContainsElement, submenu) && flyout !== skipIfContainsElement) {
                submenu.classList.add('v-hidden');
                flyout.classList.remove('active');
            }
        });
    }
    submenuElementByParentIdGet(id) {
        // dataset.parentId matching our id is the element we need to get
        return this.subMenuContainer.querySelector(`[data-parent-id="${id}"]`);
    }
    menuContainsElement(element, menu) {
        const parentIds = (element.dataset.parentIdChain || '').split(',');
        return parentIds.indexOf(menu.dataset.parentId) > -1;
    }
    activeItemGet(menu, recursive = true) {
        const activeItem = (menu || this.menuElement).querySelector(':scope > .menu-item.active');
        if (recursive && activeItem && activeItem.classList.contains('flyout')) {
            const subMenu = this.submenuElementByParentIdGet(activeItem.dataset.componentid);
            const activeChild = subMenu.querySelector(':scope > .menu-item.active');
            if (activeChild) {
                return this.activeItemGet(subMenu);
            }
        }
        return activeItem;
    }
    clearActive(menuItem) {
        if (menuItem) {
            const items = Array.from(menuItem.parentElement.querySelectorAll(':scope > .menu-item.active'));
            items.forEach(item => {
                if (item !== menuItem) {
                    item.classList.remove('active');
                    if (item.classList.contains('flyout')) {
                        const items = Array.from(item.querySelectorAll('.menu-item.active'));
                        items.forEach(item => item.classList.remove('active'));
                    }
                }
            });
        }
        else {
            const items = Array.from(this.rootElement.querySelectorAll('.menu-item.active'));
            items.forEach(item => item.classList.remove('active'));
        }
    }
    navigateMenu(direction) {
        const currentItem = this.activeItemGet();
        const items = this.getSiblingItems(currentItem);
        const newIndex = this.getNewIndex(items, direction, currentItem);
        this.updateActiveMenuItem(items, newIndex, direction);
    }
    getSiblingItems(currentItem) {
        const menu = currentItem?.parentElement || this.menuElement;
        return Array.from(menu.querySelectorAll(':scope > .menu-item'));
    }
    getNewIndex(items, direction, currentItem) {
        const currentIndex = items.indexOf(currentItem);
        if (direction === 'up' || direction === 'down') {
            return (currentIndex + (direction === 'up' ? -1 : 1) + items.length) % items.length;
        }
        return currentIndex; // Left/Right logic is already in the navigateMenu function
    }
    updateActiveMenuItem(items, newIndex, direction) {
        const newItem = items[newIndex];
        if (direction === 'up' || direction === 'down') {
            this.closeFlyouts(newItem);
        }
        items.forEach(item => item.classList.remove('active'));
        newItem.classList.add('active');
        newItem.scrollIntoView({ block: 'nearest' });
        if (newItem.classList.contains('flyout')) {
            const subMenu = this.submenuElementByParentIdGet(newItem.dataset.componentid);
            this.subMenuHover(subMenu, newItem);
            if (direction === 'right') {
                const firstSubMenuItem = subMenu?.querySelector(':scope > .menu-item');
                firstSubMenuItem.classList.add('active');
                if (firstSubMenuItem.classList.contains('flyout')) {
                    const subMenu = this.submenuElementByParentIdGet(firstSubMenuItem.dataset.componentid);
                    this.subMenuHover(subMenu, firstSubMenuItem);
                }
            }
        }
        if (direction == 'left') {
            const parentMenu = newItem.parentElement;
            if (parentMenu && parentMenu.classList.contains('sub-menu')) {
                newItem.classList.remove('active');
                if (newItem.classList.contains('flyout')) {
                    this.closeFlyouts(newItem.parentElement);
                }
                return;
            }
        }
    }
    /**
     * Trigger the onClick event of the currently active item
     */
    selectActiveItem() {
        const activeItem = this.activeItemGet();
        if (activeItem) {
            activeItem.click();
        }
    }
    template() {
        // Create a template for contextMenu
        this.rootElement.appendChild(this.menuElement);
        this.rootElement.appendChild(this.subMenuContainer);
        return '';
    }
    setMenuPosition(contextInfo) {
        const availableHeight = window.innerHeight;
        const availableWidth = window.innerWidth;
        // const x = contextInfo.x;
        // const y = contextInfo.y;
        const x = contextInfo.x ?? (contextInfo.event instanceof MouseEvent ? contextInfo.event.clientX : contextInfo.event instanceof TouchEvent ? contextInfo.event.touches[0].clientX : 0);
        const y = contextInfo.y ?? (contextInfo.event instanceof MouseEvent ? contextInfo.event.clientY : contextInfo.event instanceof TouchEvent ? contextInfo.event.touches[0].clientY : 0);
        contextInfo.x = x;
        contextInfo.y = y;
        const anchor = contextInfo.anchorPosition ?? 'top-left';
        const menuWidth = this.menuElement.offsetWidth;
        const menuHeight = this.menuElement.offsetHeight;
        const bottomOverflow = y + menuHeight > availableHeight;
        const topOverflow = y - menuHeight < 0;
        const rightOverflow = x + menuWidth > availableWidth;
        const leftOverflow = x - menuWidth < 0;
        let [menuY, menuX] = anchor.split('-');
        this.rootElement.removeAttribute('style');
        // Adjust Y position
        if ((menuY === 'top' && bottomOverflow && !topOverflow) || (menuY === 'bottom' && topOverflow && !bottomOverflow)) {
            menuY = menuY === 'top' ? 'bottom' : 'top';
        }
        if ((menuX === 'left' && rightOverflow && !leftOverflow) || (menuX === 'right' && leftOverflow && !rightOverflow)) {
            menuX = menuX === 'left' ? 'right' : 'left';
        }
        if (rightOverflow && leftOverflow) {
            const spaceRight = availableWidth - x;
            const spaceLeft = x;
            if (spaceRight > spaceLeft) {
                this.rootElement.style.left = '0';
                this.rootElement.style.right = '';
            }
            else {
                this.rootElement.style.left = '';
                this.rootElement.style.right = '0';
            }
            // menuX = spaceRight > spaceLeft ? 'left' : 'right';
        }
        else {
            this.rootElement.style.left = (menuX === 'left' ? `${x}px` : `${x - menuWidth}px`); // ternary is requied for some reason
            this.rootElement.style.right = '';
        }
        if (menuY === 'top') {
            this.menuElement.style.maxHeight = `${availableHeight - y - 15}px`;
        }
        else {
            this.menuElement.style.maxHeight = `${y - 15}px`;
        }
        this.rootElement.style.top = `${(menuY === 'top' ? y : y - menuHeight)}px`;
    }
}
ContextMenu.VERSION = '$Revision: 111977 $';
